import React, { useEffect, useState } from "react";
import Datatable from "../../EntryFile/datatable"; // Use custom Datatable
import Swal from "sweetalert2";
import { PlusIcon } from "../../EntryFile/imagePath";
import { Link } from "react-router-dom";
import { Modal, Form, Spinner } from "react-bootstrap";
import alertify from "alertifyjs";
import { DeleteIcon, EditIcon } from "../../EntryFile/imagePath";
import Tabletop from "../../EntryFile/tabletop";
import { X } from "lucide-react";
import { fetchUsers, createUser, updateUser, deleteUser } from "../../core/api/user";

const UsersCRUD = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showUserModal, setUserModal] = useState(false);
  const [showEditUserModal, setShowEditUserModal] = useState(false);
  const [userForm, setUserForm] = useState({
    firstName: "",
    surname: "",
    status: "active",
    email: "",
    role: "administration",
    password: "",
    repeatPassword: "",
  });
  const [editUserId, setEditUserId] = useState("");
  const [errors, setErrors] = useState({});
  const [totalItems, setTotalItems] = useState(0);
  const [search, setSearch] = useState(null);
  const [selectedRows, setSelectedRows] = useState([]);
  const [pageSize] = useState(10);
  const [isChangedPassowrd, setIsChangedPassword] = useState(false);
  const roles = ["super_admin", "administration"]; // Define roles
  const statuses = ["active", "inactive"]; // Define statuses

  // Fetch users from the API
  const fetchData = async ({ page, pageSize }) => {
    setLoading(true);
    try {
      const result = await fetchUsers({ page, limit: pageSize, search: search || "" });
      console.log(result, "result");
      setData(result?.data?.users);
      setTotalItems(result?.data?.totalUsers);
    } catch (error) {
      console.error(error);
      alertify.error("Failed to fetch users");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (search === null) return;
    fetchData({ page: 1, pageSize });
  }, [search]);

  // Handle user form input change
  const handleValueChange = (e) => {
    const { name, value } = e.target;
    setUserForm((prevForm) => ({ ...prevForm, [name]: value }));
    setErrors((prevErrors) => ({ ...prevErrors, [name]: "" }));
  };

  // Handle user form submit
  const handleUserSubmit = async (e) => {
    e.preventDefault();

    // Validate required fields
    const requiredFields = [
      "firstName",
      "status",
      "email",
      "role",
      "password",
      "repeatPassword",
    ];
    const newErrors = {};
    let hasErrors = false;

    requiredFields.forEach((field) => {
      if (!userForm[field]) {
        newErrors[field] = "This field is required";
        hasErrors = true;
      }
    });

    if (userForm.password !== userForm.repeatPassword) {
      newErrors["repeatPassword"] = "Passwords do not match";
      hasErrors = true;
    }

    if (hasErrors) {
      setErrors(newErrors);
      return;
    }

    try {
      await createUser(userForm);
      alertify.success("User added successfully!");
      fetchData({ page: 1, pageSize }); // Refresh the user list
      setUserModal(false); // Close the modal
      resetForm(); // Reset the form
    } catch (err) {
      alertify.error(err?.response?.data?.message);
    }
  };

  // Handle user update form submit
  const handleUserUpdateSubmit = async (e) => {
    e.preventDefault();

    // Validate required fields
    const requiredFields = [
      "firstName",
      "status",
      "email",
      "role",
    ];
    const newErrors = {};
    let hasErrors = false;

    requiredFields.forEach((field) => {
      if (!userForm[field]) {
        newErrors[field] = "This field is required";
        hasErrors = true;
      }
    });

    if (isChangedPassowrd && !userForm.password) {
      newErrors["password"] = "This field is required";
      hasErrors = true;
    }

    if (isChangedPassowrd && userForm.password !== userForm.repeatPassword) {
      newErrors["repeatPassword"] = "Passwords do not match";
      hasErrors = true;
    }

    if(!isChangedPassowrd) {
      delete userForm.password;
      delete userForm.repeatPassword;
    }


    if (hasErrors) {
      setErrors(newErrors);
      return;
    }

    try {
      await updateUser(editUserId, userForm);
      alertify.success("User updated successfully!");
      fetchData({ page: 1, pageSize }); // Refresh the user list
      setShowEditUserModal(false); // Close the modal
      resetForm(); // Reset the form
    } catch (err) {
      alertify.error(err.message);
    }
  };

  // Reset the user form
  const resetForm = () => {
    setUserForm({
      firstName: "",
      surname: "",
      status: "active",
      email: "",
      role: "administration",
      password: "",
      repeatPassword: "",
    });
    setErrors({});
  };

  // Handle user deletion
  const handleDeleteUser = async (userId) => {
    Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await deleteUser(userId);
          Swal.fire("Deleted!", "The user has been deleted.", "success");
          fetchData({ page: 1, pageSize }); // Refresh the list after deletion
        } catch (error) {
          alertify.error("Failed to delete user");
        }
      }
    });
  };


  useEffect(() => {
    fetchData({ page: 1, pageSize });
  }, []);

  // Define columns for the users table
  const columns = [
    {
      title: "User Name",
      dataIndex: "firstName",
      sorter: (a, b) => a.firstName.localeCompare(b.firstName),
    },
    {
      title: "User Surname",
      dataIndex: "surname",
      render: (surname) => surname || "-",
      sorter: (a, b) => a.surname.localeCompare(b.surname),
    },
    {
      title: "Email",
      dataIndex: "email",
      sorter: (a, b) => a.email.localeCompare(b.email),
    },
    {
      title: "Role",
      dataIndex: "role",
      sorter: (a, b) => a.role.localeCompare(b.role),
    },
    {
      title: "Created At",
      dataIndex: "createdAt",
      render: (date) => new Date(date).toLocaleDateString(),
      sorter: (a, b) => new Date(a.createdAt) - new Date(b.createdAt),
    },
    {
      title: "Status",
      dataIndex: "status",
      render: (status) => (
        <span className={`status-${status?.toLowerCase()}`}>{status}</span>
      ),
    },
    {
      title: "Actions",
      render: (record) => (
        <>
          <Link className="me-3" to="#">
            <img
              src={EditIcon}
              alt="Edit"
              onClick={() => {
                setEditUserId(record?.id);
                setUserForm({
                  firstName: record?.firstName,
                  surname: record?.surname,
                  status: record?.status,
                  email: record?.email,
                  role: record?.role,
                  password: "",
                  repeatPassword: "",
                });
                setShowEditUserModal(true);
              }}
            />
          </Link>
          <Link className="confirm-text" to="#">
            <img
              src={DeleteIcon}
              alt="Delete"
              onClick={() => handleDeleteUser(record.id)}
            />
          </Link>
        </>
      ),
    },
  ];

  return (
    <>
      <div className="page-wrapper">
        <div className="content">
          <div className="page-header">
            <div className="page-title">
              <h4>Users List</h4>
              <h6>Manage your admin users</h6>
            </div>
            <div className="page-btn">
              <Link
                to="#"
                className="btn btn-added"
                onClick={() => {
                  setUserModal(true)
                  resetForm()
                }}
              >
                <img src={PlusIcon} alt="img" className="me-1" />
                Add User
              </Link>
            </div>
          </div>

          {/* Users Table */}
          <div className="card">
            <div className="card-body">
              <div className="row">
                <Tabletop OnChange={(e) => setSearch(e.target.value)} value={search} />
              </div>
              <div className="table-responsive">
                <Datatable
                  isLoading={loading}
                  columns={columns}
                  response={{
                    data: data?.map((item, index) => ({ ...item })),
                    totalDataCount: totalItems,
                  }}
                  fetchData={fetchData}
                  NoPagination={false}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Add User Modal */}
      {
        showUserModal &&
        <Modal
          show={showUserModal}
          onHide={() => setUserModal(false)}
          centered>
          <Modal.Header>
            <Modal.Title>Add User</Modal.Title>
            <button
              type="button"
              className="btn-close me-1"
              onClick={() => setUserModal(false)}
            >
              <X size={20} onClick={() => setUserModal(false)} />
            </button>
          </Modal.Header>
          <Modal.Body>
            <Form onSubmit={handleUserSubmit}>
              <Form.Group className="form-group">
                <Form.Label>User Name</Form.Label>
                <Form.Control
                  type="text"
                  name="firstName"
                  value={userForm.firstName}
                  onChange={handleValueChange}
                  className={`form-control ${errors.firstName ? "is-invalid" : ""}`}
                />
                {errors.firstName && (
                  <div className="invalid-feedback">{errors.firstName}</div>
                )}
              </Form.Group>

              <Form.Group className="form-group">
                <Form.Label>User Surname</Form.Label>
                <Form.Control
                  type="text"
                  name="surname"
                  value={userForm.surname}
                  onChange={handleValueChange}
                  className={`form-control ${errors.surname ? "is-invalid" : ""
                    }`}
                />
                {errors.surname && (
                  <div className="invalid-feedback">{errors.surname}</div>
                )}
              </Form.Group>

              <Form.Group className="form-group">
                <Form.Label>Status</Form.Label>
                <Form.Select
                  name="status"
                  value={userForm.status}
                  onChange={handleValueChange}
                  className={`form-control ${errors.status ? "is-invalid" : ""}`}
                >
                  <option value="">Select Status</option>
                  {statuses.map((status, index) => (
                    <option key={index} value={status}>
                      {status}
                    </option>
                  ))}
                </Form.Select>
                {errors.status && (
                  <div className="invalid-feedback">{errors.status}</div>
                )}
              </Form.Group>

              <Form.Group className="form-group">
                <Form.Label>Email</Form.Label>
                <Form.Control
                  type="email"
                  name="email"
                  value={userForm.email}
                  onChange={handleValueChange}
                  className={`form-control ${errors.email ? "is-invalid" : ""}`}
                />
                {errors.email && (
                  <div className="invalid-feedback">{errors.email}</div>
                )}
              </Form.Group>

              <Form.Group className="form-group">
                <Form.Label>Role</Form.Label>
                <Form.Select
                  name="role"
                  value={userForm.role}
                  onChange={handleValueChange}
                  className={`form-control ${errors.role ? "is-invalid" : ""}`}
                >
                  <option value="">Select Role</option>
                  {roles.map((role, index) => (
                    <option key={index} value={role}>
                      {role}
                    </option>
                  ))}
                </Form.Select>
                {errors.role && (
                  <div className="invalid-feedback">{errors.role}</div>
                )}
              </Form.Group>

              <Form.Group className="form-group">
                <Form.Label>Password</Form.Label>
                <Form.Control
                  type="password"
                  name="password"
                  value={userForm.password}
                  onChange={handleValueChange}
                  className={`form-control ${errors.password ? "is-invalid" : ""}`}
                />
                {errors.password && (
                  <div className="invalid-feedback">{errors.password}</div>
                )}
              </Form.Group>

              <Form.Group className="form-group">
                <Form.Label>Repeat Password</Form.Label>
                <Form.Control
                  type="password"
                  name="repeatPassword"
                  value={userForm.repeatPassword}
                  onChange={handleValueChange}
                  className={`form-control ${errors.repeatPassword ? "is-invalid" : ""
                    }`}
                />
                {errors.repeatPassword && (
                  <div className="invalid-feedback">{errors.repeatPassword}</div>
                )}
              </Form.Group>

              <div className="d-flex justify-content-end mt-4">
                <button type="submit" className="btn btn-submit me-2">
                  Add User
                </button>
                <button
                  type="button"
                  className="btn btn-cancel"
                  onClick={() => setUserModal(false)}
                >
                  Cancel
                </button>
              </div>
            </Form>
          </Modal.Body>
        </Modal>
      }

      {/* Edit User Modal */}
      {
        showEditUserModal &&
        <Modal
          show={showEditUserModal}
          onHide={() => setShowEditUserModal(false)}
          centered
        >
          <Modal.Header>
            <Modal.Title>Edit User</Modal.Title>
            <button
              type="button"
              className="btn-close me-1"
              onClick={() => setShowEditUserModal(false)}
            >
              <X size={20} onClick={() => setShowEditUserModal(false)} />
            </button>
          </Modal.Header>
          <Modal.Body>
            <Form onSubmit={handleUserUpdateSubmit}>
              <Form.Group className="form-group">
                <Form.Label>User Name</Form.Label>
                <Form.Control
                  type="text"
                  name="firstName"
                  value={userForm.firstName}
                  onChange={handleValueChange}
                  className={`form-control ${errors.firstName ? "is-invalid" : ""}`}
                />
                {errors.firstName && (
                  <div className="invalid-feedback">{errors.firstName}</div>
                )}
              </Form.Group>

              <Form.Group className="form-group">
                <Form.Label>User Surname</Form.Label>
                <Form.Control
                  type="text"
                  name="surname"
                  value={userForm.surname}
                  onChange={handleValueChange}
                  className={`form-control ${errors.surname ? "is-invalid" : ""
                    }`}
                />
                {errors.surname && (
                  <div className="invalid-feedback">{errors.surname}</div>
                )}
              </Form.Group>

              <Form.Group className="form-group">
                <Form.Label>Status</Form.Label>
                <Form.Select
                  name="status"
                  value={userForm.status}
                  onChange={handleValueChange}
                  className={`form-control ${errors.status ? "is-invalid" : ""}`}
                >
                  <option value="">Select Status</option>
                  {statuses.map((status, index) => (
                    <option key={index} value={status}>
                      {status}
                    </option>
                  ))}
                </Form.Select>
                {errors.status && (
                  <div className="invalid-feedback">{errors.status}</div>
                )}
              </Form.Group>

              <Form.Group className="form-group">
                <Form.Label>Email</Form.Label>
                <Form.Control
                  type="email"
                  name="email"
                  value={userForm.email}
                  onChange={handleValueChange}
                  className={`form-control ${errors.email ? "is-invalid" : ""}`}
                />
                {errors.email && (
                  <div className="invalid-feedback">{errors.email}</div>
                )}
              </Form.Group>

              <Form.Group className="form-group">
                <Form.Label>Role</Form.Label>
                <Form.Select
                  name="role"
                  value={userForm.role}
                  onChange={handleValueChange}
                  className={`form-control ${errors.role ? "is-invalid" : ""}`}
                >
                  <option value="">Select Role</option>
                  {roles.map((role, index) => (
                    <option key={index} value={role}>
                      {role}
                    </option>
                  ))}
                </Form.Select>
                {errors.role && (
                  <div className="invalid-feedback">{errors.role}</div>
                )}
              </Form.Group>

              {
                isChangedPassowrd &&
                (
                  <div>
                    <Form.Group className="form-group">
                      <Form.Label>Password</Form.Label>
                      <Form.Control
                        type="password"
                        name="password"
                        value={userForm.password}
                        onChange={handleValueChange}
                        className={`form-control ${errors.password ? "is-invalid" : ""}`}
                      />
                      {errors.password && (
                        <div className="invalid-feedback">{errors.password}</div>
                      )}
                    </Form.Group>

                    <Form.Group className="form-group">
                      <Form.Label>Repeat Password</Form.Label>
                      <Form.Control
                        type="password"
                        name="repeatPassword"
                        value={userForm.repeatPassword}
                        onChange={handleValueChange}
                        className={`form-control ${errors.repeatPassword ? "is-invalid" : ""
                          }`}
                      />
                      {errors.repeatPassword && (
                        <div className="invalid-feedback">{errors.repeatPassword}</div>
                      )}
                    </Form.Group>
                  </div>
                )
              }

              <Form.Group className="form-group">
                <Form.Check
                  type="checkbox"
                  label="Change Password"
                  onChange={(e) => setIsChangedPassword(e.target.checked)}
                />
              </Form.Group>


              <div className="d-flex justify-content-end mt-4">
                <button type="submit" className="btn btn-submit me-2">
                  Update
                </button>
                <button
                  type="button"
                  className="btn btn-cancel"
                  onClick={() => setShowEditUserModal(false)}
                >
                  Cancel
                </button>
              </div>
            </Form>
          </Modal.Body>
        </Modal>
      }
    </>
  );
};

export default UsersCRUD;